import React, { useEffect } from "react";

import styled from "@emotion/styled";

import useAddressApi from "../../../../../../hooks/useAddressApi";
import { storageLocal as storage } from "../../../../../../utils/StorageHandler";
import AddressFallback from "../../../../../shared/AddressFallback";
import Field from "../../../../../shared/forms/Field";
import FormError from "../../../../../shared/forms/FormError";
import NewInput from "../../../../../shared/forms/NewInput";
import SelectInput from "../../../../../shared/forms/NewSelect";
import NewHeadline from "../../../../../shared/NewHeadline";
import useNotificationStore from "../../../../../shared/Notification/store";
import useCalculatorStore from "../../../CalculatorPage/store";
import useStepDeliveryAddress from "../store";

const AmountFirstUnloadingPoint = styled.div`
    padding-top: 20px;
    text-align: right;
`;

const AddressInputWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const StreetRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: 5fr 1fr;
       gap: 10px;
    `}
`;

const CityRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: 1fr 5fr;
       gap: 10px;
    `}
`;

const Zipcode = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1em;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

const DeliveryAddress = () => {
    const [zipcode] = useCalculatorStore((state) => [state.zipcode]);
    const { notify } = useNotificationStore();
    const [
        up,
        deliveryAddress,
        setDeliveryAddress,
        changeSelectedStreet,
        changeSelectedCity,
        amountFirstUnloadingPoint,
        hasErrorAt,
        addError,
        clearError,
        getAmountForFirstUnloadingPoint,
        additionalAddresses,
        setAmountFirstUnloadingPoint,
        onGooglePlacesChange,
        hadAddressApiError,
        setHadApiError,
    ] = useStepDeliveryAddress((state) => [
        state.up,
        state.deliveryAddress,
        state.setDeliveryAddress,
        state.changeSelectedStreet,
        state.changeSelectedCity,
        state.amountFirstUnloadingPoint,
        state.hasErrorAt,
        state.addError,
        state.clearError,
        state.getAmountForFirstUnloadingPoint,
        state.additionalAddresses,
        state.setAmountFirstUnloadingPoint,
        state.onGooglePlacesChange,
        state.hadAddressApiError,
        state.setHadAddressApiError,
    ]);

    const { streets, cities, addressApiError } = useAddressApi({
        zipcode: zipcode,
        city: deliveryAddress.city,
    });

    useEffect(() => {
        if (addressApiError) {
            notify("Es ist ein Fehler bei der Adresssuche aufgetreten");
            setHadApiError();
        }
    }, [addressApiError]);

    useEffect(() => {
        setAmountFirstUnloadingPoint(
            getAmountForFirstUnloadingPoint(storage.getInt("hel:amount"))
        );
    }, [additionalAddresses]);

    const handleHouseNumberChange = (event) => {
        setDeliveryAddress({
            ...deliveryAddress,
            house_number: event.target.value,
        });
    };

    const handleCityChange = (event) => {
        changeSelectedStreet("");
        changeSelectedCity(event.target.value ?? "");
    };

    const handleStreetChange = (event) => {
        changeSelectedStreet(event.target.value ?? "");
    };

    return (
        <React.Fragment>
            {up === 1 ? (
                <NewHeadline>Lieferadresse</NewHeadline>
            ) : (
                <div className="columns is-mobile">
                    <div className="column">
                        <NewHeadline>Haushalt 1</NewHeadline>
                    </div>
                    <AmountFirstUnloadingPoint className="column">
                        {amountFirstUnloadingPoint}&nbsp;l
                    </AmountFirstUnloadingPoint>
                </div>
            )}
            <Field>
                {!hadAddressApiError ? (
                    <AddressInputWrapper>
                        <CityRow>
                            <Zipcode>{deliveryAddress.zipcode}</Zipcode>
                            <div>
                                <SelectInput
                                    name="city"
                                    placeholder="Bitte wähle Deinen Ort aus"
                                    withDefault
                                    options={cities.map((city) => ({
                                        value: city.ONAME,
                                        text: city.ONAME,
                                    }))}
                                    value={
                                        cities.find((city) => {
                                            return (
                                                city.ONAME ===
                                                deliveryAddress.city
                                            );
                                        })?.ONAME ?? ""
                                    }
                                    onChange={handleCityChange}
                                />
                                {hasErrorAt("deliveryAddress.city") && (
                                    <FormError
                                        message={"Bitte wähle einen Ort aus."}
                                    />
                                )}
                            </div>
                        </CityRow>

                        <StreetRow>
                            <div>
                                <SelectInput
                                    name="street"
                                    placeholder="Bitte wähle Deine Straße aus"
                                    withDefault
                                    options={streets.map((street) => {
                                        return {
                                            value: street,
                                            text: street,
                                        };
                                    })}
                                    value={
                                        streets.find(
                                            (street) =>
                                                street ===
                                                deliveryAddress.street
                                        ) ?? ""
                                    }
                                    onChange={handleStreetChange}
                                />
                                {hasErrorAt("deliveryAddress.street") && (
                                    <FormError
                                        message={"Bitte wähle eine Straße aus."}
                                    />
                                )}
                            </div>

                            <NewInput
                                placeholder="Haus.-Nr."
                                id="house_number"
                                name="house_number"
                                onChange={handleHouseNumberChange}
                                value={deliveryAddress.house_number}
                                validation="house_number"
                                forceError={hasErrorAt(
                                    "deliveryAddress.house_number"
                                )}
                                onError={() =>
                                    addError("deliveryAddress.house_number")
                                }
                                onErrorSolved={() =>
                                    clearError("deliveryAddress.house_number")
                                }
                            />
                        </StreetRow>
                    </AddressInputWrapper>
                ) : (
                    <AddressFallback
                        onGooglePlacesChange={onGooglePlacesChange}
                        zipcode={deliveryAddress.zipcode}
                        handleHouseNumberChange={handleHouseNumberChange}
                        houseNumber={deliveryAddress.house_number}
                        houseNumberHasErrorAt={hasErrorAt(
                            "deliveryAddress.house_number"
                        )}
                        houseNumberAddError={() =>
                            addError("deliveryAddress.house_number")
                        }
                        houseNumberClearError={() =>
                            clearError("deliveryAddress.house_number")
                        }
                    />
                )}
            </Field>
        </React.Fragment>
    );
};

export default DeliveryAddress;
